import React from 'react';
import './FocusModeHUD.css';

// Tabler Brain Icon
const BrainIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15.5 13a3.5 3.5 0 0 0 -3.5 3.5v1a3.5 3.5 0 0 0 7 0v-1.8" />
    <path d="M8.5 13a3.5 3.5 0 0 1 3.5 3.5v1a3.5 3.5 0 0 1 -7 0v-1.8" />
    <path d="M17.5 16a3.5 3.5 0 0 0 0 -7h-.5" />
    <path d="M19 9.3v-2.8a3.5 3.5 0 0 0 -7 0" />
    <path d="M6.5 16a3.5 3.5 0 0 1 0 -7h.5" />
    <path d="M5 9.3v-2.8a3.5 3.5 0 0 1 7 0v10" />
  </svg>
);

/**
 * FocusModeHUD Component
 * 
 * A minimal floating HUD that appears during focus mode to maintain context.
 * Shows campaign name, focus context, and progress without overwhelming the focused view.
 */
const FocusModeHUD = ({
  campaignName,
  progress,
  focusedCount,      // Number of originally selected tasks
  totalVisibleCount, // Total visible including dependencies
  onExitFocus,
  isVisible,
  isEntering,
  isExiting,
}) => {
  // Don't render if not visible and not animating
  const shouldRender = isVisible || isEntering || isExiting;
  if (!shouldRender) return null;
  
  const containerClasses = [
    'focus-mode-hud',
    !isVisible && 'focus-mode-hud--hidden',
  ].filter(Boolean).join(' ');
  
  const dependencyCount = totalVisibleCount - focusedCount;
  
  // Build focus context string
  const getFocusContext = () => {
    if (focusedCount === 1) {
      if (dependencyCount === 0) {
        return '1 task';
      }
      return `1 task + ${dependencyCount} ${dependencyCount === 1 ? 'dependency' : 'dependencies'}`;
    }
    if (dependencyCount === 0) {
      return `${focusedCount} tasks`;
    }
    return `${focusedCount} tasks + ${dependencyCount} ${dependencyCount === 1 ? 'dependency' : 'dependencies'}`;
  };
  
  return (
    <div className={containerClasses}>
      <div className="focus-mode-hud__content">
        {/* Campaign context */}
        <div className="focus-mode-hud__campaign">
          <span className="focus-mode-hud__campaign-name">{campaignName}</span>
          <span className="focus-mode-hud__separator">•</span>
          <span className="focus-mode-hud__progress">{Math.round(progress)}%</span>
        </div>
        
        {/* Focus context */}
        <div className="focus-mode-hud__focus-info">
          <BrainIcon className="focus-mode-hud__focus-icon" />
          <span className="focus-mode-hud__focus-text">
            Focusing on {getFocusContext()}
          </span>
        </div>
        
        {/* Mini progress bar */}
        <div className="focus-mode-hud__progress-bar">
          <div 
            className="focus-mode-hud__progress-fill" 
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
      
      {/* Exit button */}
      <button 
        className="focus-mode-hud__exit-btn"
        onClick={onExitFocus}
        title="Exit Focus Mode (Esc)"
      >
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round"/>
        </svg>
      </button>
    </div>
  );
};

export default FocusModeHUD;

